export const DIMENSIONS = {
  position: { name: 'Position', key: 'p', color: '#3b82f6' },
  audio: { name: 'Audio (Letter)', key: 'a', color: '#10b981' },
  voice: { name: 'Voice (Speaker)', key: 'v', color: '#14b8a6' },
  note: { name: 'Musical Note', key: 'm', color: '#84cc16' },
  color: { name: 'Color', key: 'c', color: '#f59e0b' },
  shape: { name: 'Shape', key: 's', color: '#8b5cf6' },
  number: { name: 'Number', key: 'n', color: '#ec4899' },
  face: { name: 'Face', key: 'f', color: '#f97316' },
  arrow: { name: 'Arrow', key: 'r', color: '#06b6d4' },
  movement: { name: 'Movement', key: 'o', color: '#a855f7' },
  pattern: { name: 'Pattern', key: 't', color: '#eab308' },
  border: { name: 'Border', key: 'b', color: '#06b6d4' },
  orbitalPosition: { name: 'Orbital Position', key: 'l', color: '#ef4444' },
  circleSize: { name: 'Circle Size', key: 'z', color: '#10b981' },
  circleRotation: { name: 'Circle Rotation', key: 'x', color: '#8b5cf6' },
  circleBrightness: { name: 'Circle Brightness', key: 'i', color: '#f59e0b' },
  categoryText: { name: 'Category (Text)', key: 'w', color: '#ec4899' },
  categoryAudio: { name: 'Category (Audio)', key: 'e', color: '#f97316' },
  nameText: { name: 'Name (Text)', key: 'y', color: '#8b5cf6' },
  nameAudio: { name: 'Name (Audio)', key: 'u', color: '#06b6d4' },
  math: { name: 'Math', key: 'q', color: '#eab308' },
  rhythm: { name: 'Rhythm', key: 'h', color: '#a78bfa' },
  mandarinTone: { name: 'Mandarin Tone', key: 'd', color: '#f472b6' },
  volume: { name: 'Volume', key: 'g', color: '#06b6d4' }
};

export const DIMENSION_DESCRIPTIONS = {
  position: 'Track which square is lit',
  audio: 'Track which letter is spoken',
  voice: 'Track which speaker voice',
  note: 'Track which musical note (C-B)',
  color: 'Track the square color',
  shape: 'Track the shape shown',
  number: 'Track the number shown',
  face: 'Track the facial expression',
  arrow: 'Track the arrow direction',
  movement: 'Track the movement direction',
  pattern: 'Track the border pattern (6 styles)',
  border: 'Track which edge glows',
  orbitalPosition: 'Track circle location around board',
  circleSize: 'Track the circle size (5 levels)',
  circleRotation: 'Track the circle orientation',
  circleBrightness: 'Track the circle brightness (4 levels)',
  categoryText: 'Track category type (same category = match)',
  categoryAudio: 'Track category type (same category = match)',
  nameText: 'Track name shown (8 names)',
  nameAudio: 'Track name spoken (8 names)',
  math: 'Track equation answer (0-9)',
  rhythm: 'Track rhythmic pattern (8 patterns, pairs with Note)',
  mandarinTone: 'Track Mandarin tone (5 tones, 12 words each - 60 total)',
  volume: 'Track audio volume/loudness (3 levels: low/medium/high)'
};

export const LETTERS = ['C', 'H', 'K', 'L', 'Q', 'R', 'S', 'T'];

// Musical notes spread across octaves for better differentiation
// Using circle of fifths pattern shifted up a half octave (6 semitones)
export const NOTES = ['F#2', 'C#3', 'G#3', 'D#4', 'A#4', 'F5', 'B5'];

export const NOTE_FREQUENCIES = {
  'F#2': 92.50,   // Low F#
  'C#3': 138.59,  // Low-mid C#
  'G#3': 207.65,  // Mid G#
  'D#4': 311.13,  // Mid-high D#
  'A#4': 466.16,  // High A#
  'F5': 698.46,   // High F
  'B5': 987.77    // Very high B
};

// Rhythm patterns - arrays of note durations in milliseconds (max 2000ms total)
// Each rhythm is a sequence of [duration, pause] pairs
export const RHYTHMS = [
  { id: 0, pattern: [200, 200, 200, 200, 200], label: 'Even Quarter' },        // 5 even beats
  { id: 1, pattern: [150, 100, 150, 100, 150, 100, 150, 100], label: 'Fast Eighth' },  // Quick beats
  { id: 2, pattern: [400, 200, 400], label: 'Long-Short-Long' },               // 3 varied beats
  { id: 3, pattern: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100], label: 'Rapid Sixteenth' }, // Very fast
  { id: 4, pattern: [300, 100, 100, 100, 300], label: 'Long-Triple-Long' },   // Gallop pattern
  { id: 5, pattern: [200, 100, 300, 100, 200], label: 'Syncopated' },         // Off-beat
  { id: 6, pattern: [500, 200, 500], label: 'Half Notes' },                    // Slow, steady
  { id: 7, pattern: [250, 150, 100, 150, 250], label: 'Accelerando' }         // Speed variation
];

// Mandarin tone patterns (for audio dimension)
// Represents the 4 Mandarin tones plus neutral tone
export const MANDARIN_TONES = [
  { id: 0, name: 'First Tone (High Flat)', pitch: 'high-flat' },      // 1st tone: ā - high, level
  { id: 1, name: 'Second Tone (Rising)', pitch: 'rising' },           // 2nd tone: á - rising
  { id: 2, name: 'Third Tone (Dip)', pitch: 'dip-rise' },            // 3rd tone: ǎ - falling then rising
  { id: 3, name: 'Fourth Tone (Falling)', pitch: 'falling' },         // 4th tone: à - sharp falling
  { id: 4, name: 'Neutral Tone', pitch: 'neutral' }                   // 5th/neutral tone: a - light, neutral
];

// Mandarin Tone Words - Real Chinese characters for each tone
// Each tone has multiple valid Chinese words covering different sound combinations
// Matching is by tone type, not exact word (like category dimension)
export const MANDARIN_TONE_WORDS = {
  // First tone (high-flat) - 阴平 (yīn píng)
  tone1: [
    '妈', // mā - mother
    '八', // bā - eight
    '他', // tā - he/him
    '多', // duō - many
    '哥', // gē - elder brother
    '花', // huā - flower
    '加', // jiā - add
    '沙', // shā - sand
    '车', // chē - vehicle
    '高', // gāo - tall
    '飞', // fēi - fly
    '书'  // shū - book
  ],
  
  // Second tone (rising) - 阳平 (yáng píng)
  tone2: [
    '麻', // má - numb
    '拔', // bá - pull out
    '达', // dá - reach
    '茶', // chá - tea
    '河', // hé - river
    '华', // huá - magnificent
    '爬', // pá - climb
    '蛇', // shé - snake
    '盒', // hé - box
    '鱼', // yú - fish
    '楼', // lóu - building
    '头'  // tóu - head
  ],
  
  // Third tone (dip-rise) - 上声 (shǎng shēng)
  tone3: [
    '马', // mǎ - horse
    '把', // bǎ - handle
    '打', // dǎ - hit
    '洒', // sǎ - sprinkle
    '卡', // kǎ - stuck
    '假', // jiǎ - false
    '果', // guǒ - fruit
    '火', // huǒ - fire
    '好', // hǎo - good
    '小', // xiǎo - small
    '老', // lǎo - old
    '口'  // kǒu - mouth
  ],
  
  // Fourth tone (falling) - 去声 (qù shēng)
  tone4: [
    '骂', // mà - scold
    '爸', // bà - father
    '大', // dà - big
    '怕', // pà - afraid
    '辣', // là - spicy
    '下', // xià - down
    '过', // guò - pass
    '贺', // hè - congratulate
    '快', // kuài - fast
    '饭', // fàn - rice/meal
    '路', // lù - road
    '是'  // shì - is
  ],
  
  // Neutral tone (light/unstressed) - 轻声 (qīng shēng)
  tone5: [
    '吗', // ma - question particle
    '吧', // ba - suggestion particle
    '啦', // la - exclamation particle
    '呢', // ne - question particle
    '的', // de - possessive particle
    '了', // le - completed action particle
    '着', // zhe - continuous action particle
    '个', // ge - general classifier
    '们', // men - plural suffix
    '子', // zi - noun suffix
    '地', // de - adverbial particle
    '得'  // de - complement particle
  ]
};

// Flat array of all Mandarin tone words for random selection
export const ALL_MANDARIN_WORDS = [
  ...MANDARIN_TONE_WORDS.tone1,
  ...MANDARIN_TONE_WORDS.tone2,
  ...MANDARIN_TONE_WORDS.tone3,
  ...MANDARIN_TONE_WORDS.tone4,
  ...MANDARIN_TONE_WORDS.tone5
];

// Get tone category of a Mandarin word
export const getToneOfWord = (word) => {
  for (const [tone, words] of Object.entries(MANDARIN_TONE_WORDS)) {
    if (words.includes(word)) {
      return tone;
    }
  }
  return null;
};

export const COLORS = [
  '#ef4444',  // Bright Red
  '#60a5fa',  // Blue
  '#fbbf24',  // Yellow
  '#8b5cf6',  // Purple
  '#000000',  // Black
  '#ffffff',  // White
  '#22c55e'   // Green
];

export const SHAPES = ['circle', 'square', 'triangle', 'diamond', 'pentagon', 'hexagon', 'star', 'cross'];

export const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export const FACES = ['😀', '😢', '😡', '😮', '😐', '😴', '😱'];

export const ARROWS = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];

export const MOVEMENTS = ['up', 'down', 'left', 'right', 'up-right', 'up-left', 'down-right', 'down-left'];

export const PATTERNS = ['solid', 'dashed', 'dotted', 'double', 'zigzag', 'wavy'];

export const BORDERS = ['top', 'right', 'bottom', 'left'];

export const VOICE_TYPES = ['male1', 'male2', 'female1', 'female2'];

// Volume levels for audio dimensions (loudness/amplitude)
// Separate scales for spoken audio vs musical notes due to perceptual differences
// EXTREME RANGES with massive multipliers for unmistakable differentiation
// Now using Web Audio API with GainNode - supports volumes > 1.0!

// Spoken audio (letters, voice, categories, names) - EXTREME range
// When tracking voice+volume or audio+volume, need MASSIVE differences
export const VOLUMES_SPOKEN = [0.06, 0.9, 7]; // Low, Medium, High
// Ratios: 12.5x (low→med), 15x (med→high), 187.5x total range!

// Musical notes (note, rhythm, volume-only) - very wide range
// Synthesized tones need extreme range too for multi-dimension tracking
export const VOLUMES_MUSICAL = [0.15, 2.5, 20.0]; // Low, Medium, High  
// Ratios: 6.67x (low→med), 10x (med→high), 66.7x total range

// Default VOLUMES array for backward compatibility
// Uses musical range because volume-only plays a tone (not spoken audio)
export const VOLUMES = VOLUMES_MUSICAL;

// Orbital circle dimensions
export const ORBITAL_POSITIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']; // 8 compass points
export const CIRCLE_SIZES = ['tiny', 'small', 'medium', 'large', 'huge']; // 5 sizes
export const CIRCLE_ROTATIONS = [0, 45, 90, 135, 180, 225, 270, 315]; // 8 orientations in degrees
export const CIRCLE_BRIGHTNESS = [0, 0.333, 0.667, 1.0]; // 4 brightness levels (0%, 33%, 67%, 100%)

// Category words - 64 total words, 8 words per category (8 categories)
// Words are chosen to be unambiguously in one category only
export const CATEGORY_WORDS = {
  ANIMAL: ['DOG', 'CAT', 'LION', 'TIGER', 'BEAR', 'WOLF', 'DEER', 'MOUSE'],
  FRUIT: ['APPLE', 'PEAR', 'BANANA', 'GRAPE', 'LEMON', 'PEACH', 'CHERRY', 'MANGO'],
  TOOL: ['HAMMER', 'DRILL', 'SAW', 'WRENCH', 'PLIERS', 'SHOVEL', 'KNIFE', 'AXE'],
  COLOR: ['RED', 'BLUE', 'GREEN', 'YELLOW', 'PURPLE', 'WHITE', 'PINK', 'BROWN'],
  VEHICLE: ['CAR', 'BUS', 'TRUCK', 'TRAIN', 'AIRPLANE', 'BOAT', 'BIKE', 'VAN'],
  SPORT: ['SOCCER', 'TENNIS', 'GOLF', 'HOCKEY', 'BOXING', 'FOOTBALL', 'BASEBALL', 'SWIMMING'],
  MUSIC: ['PIANO', 'GUITAR', 'DRUMS', 'VIOLIN', 'FLUTE', 'TRUMPET', 'HARP', 'CELLO'],
  BODY: ['HAND', 'FOOT', 'HEAD', 'ARM', 'LEG', 'NOSE', 'EAR', 'EYE']
};

// Get all words as flat array for random selection
export const ALL_CATEGORY_WORDS = Object.values(CATEGORY_WORDS).flat();

// Helper to get category of a word
export const getCategoryOfWord = (word) => {
  for (const [category, words] of Object.entries(CATEGORY_WORDS)) {
    if (words.includes(word)) {
      return category;
    }
  }
  return null;
};

export const NAMES = ['ANNA', 'MIKE', 'SARA', 'JOHN', 'EMMA', 'PAUL', 'LUCY', 'MARK']; // 8 names (4 letters each)

// Math dimension - multiple equations per answer (0-9)
// Each answer has many different equations to vary the matches and prevent memorization
// Includes addition, subtraction, multiplication, and division
// No '0' in operands (makes it too easy), but equations can equal 0
// All equations verified to be correct
export const MATH_EQUATIONS_BY_ANSWER = {
  // Answer: 0 (40+ equations)
  0: ['1-1', '2-2', '3-3', '4-4', '5-5', '6-6', '7-7', '8-8', '9-9', '10-10', '11-11', '12-12', '13-13', '14-14', '15-15', '16-16', '17-17', '18-18', '19-19', '20-20', '21-21', '22-22', '23-23', '24-24', '25-25', '30-30', '35-35', '40-40', '45-45', '50-50', '60-60', '70-70', '80-80', '90-90', '99-99'],
  
  // Answer: 1 (35+ equations)
  1: ['2-1', '3-2', '4-3', '5-4', '6-5', '7-6', '8-7', '9-8', '10-9', '11-10', '12-11', '13-12', '14-13', '15-14', '16-15', '17-16', '18-17', '19-18', '20-19', '25-24', '30-29', '40-39', '50-49', '60-59', '1×1', '2÷2', '3÷3', '4÷4', '5÷5', '6÷6', '7÷7', '8÷8', '9÷9', '10÷10', '99÷99'],
  
  // Answer: 2 (40+ equations)
  2: ['1+1', '3-1', '4-2', '5-3', '6-4', '7-5', '8-6', '9-7', '10-8', '11-9', '12-10', '13-11', '14-12', '15-13', '16-14', '17-15', '18-16', '19-17', '20-18', '25-23', '30-28', '40-38', '50-48', '1×2', '2×1', '4÷2', '6÷3', '8÷4', '10÷5', '12÷6', '14÷7', '16÷8', '18÷9', '20÷10', '22÷11', '24÷12', '26÷13', '28÷14', '30÷15', '40÷20', '50÷25', '60÷30', '80÷40', '90÷45', '100÷50'],
  
  // Answer: 3 (45+ equations)
  3: ['1+2', '2+1', '4-1', '5-2', '6-3', '7-4', '8-5', '9-6', '10-7', '11-8', '12-9', '13-10', '14-11', '15-12', '16-13', '17-14', '18-15', '19-16', '20-17', '25-22', '30-27', '40-37', '50-47', '1×3', '3×1', '6÷2', '9÷3', '12÷4', '15÷5', '18÷6', '21÷7', '24÷8', '27÷9', '30÷10', '33÷11', '36÷12', '39÷13', '42÷14', '45÷15', '60÷20', '75÷25', '90÷30', '120÷40'],
  
  // Answer: 4 (50+ equations)
  4: ['2+2', '1+3', '3+1', '5-1', '6-2', '7-3', '8-4', '9-5', '10-6', '11-7', '12-8', '13-9', '14-10', '15-11', '16-12', '17-13', '18-14', '19-15', '20-16', '25-21', '30-26', '40-36', '50-46', '1×4', '2×2', '4×1', '8÷2', '12÷3', '16÷4', '20÷5', '24÷6', '28÷7', '32÷8', '36÷9', '40÷10', '44÷11', '48÷12', '52÷13', '56÷14', '60÷15', '80÷20', '100÷25', '120÷30', '160÷40'],
  
  // Answer: 5 (50+ equations)
  5: ['2+3', '3+2', '1+4', '4+1', '6-1', '7-2', '8-3', '9-4', '10-5', '11-6', '12-7', '13-8', '14-9', '15-10', '16-11', '17-12', '18-13', '19-14', '20-15', '25-20', '30-25', '40-35', '50-45', '60-55', '1×5', '5×1', '10÷2', '15÷3', '20÷4', '25÷5', '30÷6', '35÷7', '40÷8', '45÷9', '50÷10', '55÷11', '60÷12', '65÷13', '70÷14', '75÷15', '100÷20', '125÷25', '150÷30'],
  
  // Answer: 6 (55+ equations)
  6: ['3+3', '2+4', '4+2', '1+5', '5+1', '7-1', '8-2', '9-3', '10-4', '11-5', '12-6', '13-7', '14-8', '15-9', '16-10', '17-11', '18-12', '19-13', '20-14', '25-19', '30-24', '40-34', '50-44', '60-54', '1×6', '2×3', '3×2', '6×1', '12÷2', '18÷3', '24÷4', '30÷5', '36÷6', '42÷7', '48÷8', '54÷9', '60÷10', '66÷11', '72÷12', '78÷13', '84÷14', '90÷15', '120÷20', '150÷25', '180÷30'],
  
  // Answer: 7 (50+ equations)
  7: ['3+4', '4+3', '2+5', '5+2', '1+6', '6+1', '8-1', '9-2', '10-3', '11-4', '12-5', '13-6', '14-7', '15-8', '16-9', '17-10', '18-11', '19-12', '20-13', '25-18', '30-23', '40-33', '50-43', '60-53', '1×7', '7×1', '14÷2', '21÷3', '28÷4', '35÷5', '42÷6', '49÷7', '56÷8', '63÷9', '70÷10', '77÷11', '84÷12', '91÷13', '98÷14', '140÷20', '175÷25'],
  
  // Answer: 8 (55+ equations)
  8: ['4+4', '3+5', '5+3', '2+6', '6+2', '1+7', '7+1', '9-1', '10-2', '11-3', '12-4', '13-5', '14-6', '15-7', '16-8', '17-9', '18-10', '19-11', '20-12', '25-17', '30-22', '40-32', '50-42', '60-52', '1×8', '2×4', '4×2', '8×1', '16÷2', '24÷3', '32÷4', '40÷5', '48÷6', '56÷7', '64÷8', '72÷9', '80÷10', '88÷11', '96÷12', '160÷20', '200÷25', '240÷30'],
  
  // Answer: 9 (55+ equations)
  9: ['4+5', '5+4', '3+6', '6+3', '2+7', '7+2', '1+8', '8+1', '10-1', '11-2', '12-3', '13-4', '14-5', '15-6', '16-7', '17-8', '18-9', '19-10', '20-11', '25-16', '30-21', '40-31', '50-41', '60-51', '70-61', '1×9', '3×3', '9×1', '18÷2', '27÷3', '36÷4', '45÷5', '54÷6', '63÷7', '72÷8', '81÷9', '90÷10', '99÷11', '180÷20', '225÷25', '270÷30']
};

// Helper to get a random equation for a given answer
export const getRandomEquationForAnswer = (answer) => {
  const equations = MATH_EQUATIONS_BY_ANSWER[answer];
  return equations[Math.floor(Math.random() * equations.length)];
};

